import { ApolloCache, ApolloClient, useFragment } from '@apollo/client';
import { useEffect, useMemo } from 'react';

import {
  GetProjectTeamSubscriptionDocument,
  GetProjectTeamSubscriptionQuery,
  GetProjectTeamSubscriptionQueryVariables,
  ListTeamSubscriptionsDocument,
  ListTeamSubscriptionsQuery,
  TeamSubscriptionFragment,
  TeamSubscriptionFragmentDoc,
  useGetProjectTeamSubscriptionQuery,
  useListTeamSubscriptionsQuery,
} from '@graphql/generated';
import { logError } from '@utils/logNonFatalError';

type ProjectWithTeam = {
  id?: string | null;
  team?: {
    id?: string | null;
    personal?: boolean | null;
    subscriptionDetails?: TeamSubscriptionFragment['subscriptionDetails'];
  } | null;
};

export type ProjectTeamSubscription<P> = {
  project: P | null | undefined;
  /**
   * True while the team's billing is unknown: still loading, or the request
   * failed. `shouldShowPaywallForProject` reads missing billing as "no
   * paywall", so paywall-gated actions must stay unavailable until this is
   * false, or an expired team gets through (TA-10958 review).
   */
  billingPending: boolean;
};

// Projects whose getProject fallback already reported missing billing, so a
// list with many rows on that project sends one Sentry event, not one per row.
const reportedUnresolvedProjects = new Set<string>();

const needsBillingLookup = (project: ProjectWithTeam | null | undefined) =>
  !!project?.team?.id && project.team.subscriptionDetails === undefined;

/**
 * TA-10958: listTasks rows no longer carry `project.team.subscriptionDetails`
 * (it was repeated on every row). This puts it back on the project from the
 * team's cache entry, so callers can keep passing the result to
 * `shouldShowPaywallForProject` and `setPaywallTeam`.
 *
 * - A project that already has `subscriptionDetails` (getTask, getProject)
 *   is returned unchanged. `null` counts as present: it means "no plan".
 * - Otherwise the team comes from `listTeamSubscriptions`, one request for
 *   every team the user belongs to, shared by all rows through the cache.
 * - A team missing from that list (the user is only a collaborator on the
 *   task) is fetched once through getProject.
 *
 * Until the data arrives the project is returned unchanged and
 * `billingPending` is true; callers keep paywall-gated actions disabled
 * meanwhile. A failed request leaves it true (fail closed) until a later
 * `useRefreshTeamSubscriptions` fetch fills the cache. Nothing refreshes the
 * getProject fallback, so when it finishes without billing the row stays
 * disabled until it remounts; that case is logged to Sentry so it is not
 * silent (TA-10958 review).
 */
export const useProjectTeamSubscription = <P extends ProjectWithTeam>(
  project: P | null | undefined
): ProjectTeamSubscription<P> => {
  const teamId = project?.team?.id ?? undefined;
  const needsLookup = needsBillingLookup(project);

  const { data: teamsData } = useListTeamSubscriptionsQuery({
    fetchPolicy: 'cache-first',
    skip: !needsLookup,
  });

  const inTeamList = !!teamsData?.listTeams.some((t) => t.id === teamId);
  const needsProjectFallback =
    needsLookup && !!teamsData && !inTeamList && !!project?.id;

  const fallback = useGetProjectTeamSubscriptionQuery({
    variables: { id: project?.id ?? '' },
    fetchPolicy: 'cache-first',
    skip: !needsProjectFallback,
  });

  const { data: team, complete } = useFragment({
    fragment: TeamSubscriptionFragmentDoc,
    fragmentName: 'TeamSubscription',
    from: { __typename: 'Team', id: teamId ?? '' },
  });

  const subscriptionDetails =
    needsLookup && complete ? team.subscriptionDetails : undefined;

  const fallbackFinishedWithoutBilling =
    needsProjectFallback &&
    !fallback.loading &&
    (!!fallback.error || !!fallback.data) &&
    subscriptionDetails === undefined;

  useEffect(() => {
    const projectId = project?.id;
    if (!fallbackFinishedWithoutBilling || !projectId) return;
    if (reportedUnresolvedProjects.has(projectId)) return;
    reportedUnresolvedProjects.add(projectId);
    logError({
      message: 'Team billing unresolved for non-member project',
      errorMessage:
        fallback.error?.message ?? 'getProject returned no team billing',
      operationName: 'getProjectTeamSubscription',
      extra: [{ projectId, teamId }],
    });
  }, [fallbackFinishedWithoutBilling, fallback.error, project?.id, teamId]);

  return useMemo(() => {
    if (!project?.team || subscriptionDetails === undefined) {
      return { project, billingPending: needsLookup };
    }
    return {
      project: {
        ...project,
        team: { ...project.team, subscriptionDetails },
      } as P,
      billingPending: false,
    };
  }, [project, subscriptionDetails, needsLookup]);
};

/**
 * Synchronous cache read behind `resolveProjectTeamSubscription`: merges the
 * team's cached billing into the project, or returns it unchanged when the
 * team is not cached. It never fetches, so handlers should call the resolver
 * rather than this directly.
 */
export const withCachedTeamSubscription = <P extends ProjectWithTeam>(
  cache: ApolloCache<unknown>,
  project: P | null | undefined
): P | null | undefined => {
  const teamId = project?.team?.id;
  if (!project?.team || !teamId) return project;
  if (project.team.subscriptionDetails !== undefined) return project;

  const team = cache.readFragment<TeamSubscriptionFragment>({
    id: cache.identify({ __typename: 'Team', id: teamId }),
    fragment: TeamSubscriptionFragmentDoc,
    fragmentName: 'TeamSubscription',
  });
  if (!team) return project;

  return {
    ...project,
    team: { ...project.team, subscriptionDetails: team.subscriptionDetails },
  } as P;
};

/**
 * Click-time counterpart of `useProjectTeamSubscription`, for handlers that
 * check the paywall on a task they were handed (bulk actions). Uses the
 * cached billing when there is one; otherwise waits for listTeamSubscriptions
 * (and the getProject fallback for a non-member team) before answering, so
 * the check never runs on missing billing. `billingPending` stays true only
 * when those requests failed; callers then refuse the action.
 */
export const resolveProjectTeamSubscription = async <P extends ProjectWithTeam>(
  client: ApolloClient<unknown>,
  project: P | null | undefined
): Promise<ProjectTeamSubscription<P>> => {
  const cached = withCachedTeamSubscription(client.cache, project);
  if (!needsBillingLookup(cached)) {
    return { project: cached, billingPending: false };
  }

  try {
    const { data } = await client.query<ListTeamSubscriptionsQuery>({
      query: ListTeamSubscriptionsDocument,
      fetchPolicy: 'cache-first',
    });
    const teamId = project?.team?.id;
    const inTeamList = !!data?.listTeams.some((t) => t.id === teamId);
    if (!inTeamList && project?.id) {
      await client.query<
        GetProjectTeamSubscriptionQuery,
        GetProjectTeamSubscriptionQueryVariables
      >({
        query: GetProjectTeamSubscriptionDocument,
        variables: { id: project.id },
        fetchPolicy: 'cache-first',
      });
    }
  } catch {
    // Billing stays unknown; the cache read below reports it as pending.
  }

  const resolved = withCachedTeamSubscription(client.cache, project);
  return { project: resolved, billingPending: needsBillingLookup(resolved) };
};

/**
 * Refreshes every team's billing state from the network. Mount it once on a
 * task-list surface so the per-row `useProjectTeamSubscription` reads, which
 * are cache-first, stay as fresh as the old per-row field was. Pass the
 * task list's own `pollInterval` so billing refreshes on the same
 * foreground cadence as `listTasks` (TA-10958 review); the old per-row field
 * was refreshed by every listTasks poll.
 */
export const useRefreshTeamSubscriptions = ({
  pollInterval,
}: { pollInterval?: number } = {}) => {
  useListTeamSubscriptionsQuery({
    fetchPolicy: 'cache-and-network',
    pollInterval,
  });
};
